// SEO-12 · /resources/school-transport-seat-safety
//
// EDITORIAL GROUNDING (see docs/marketing/RESOURCES_EDITORIAL_PROCESS.md):
//   • Claims registry D4 (⚠ — approved wording only): allocation is REFUSED above configured seat
//     capacity (waitlist instead); document-expiry alerts on RC/insurance/permit/PUC/fitness/
//     licence; live position carries an honest live-or-stale badge; the boarding log is append-only;
//     crew logins come ONLY from the explicit link action. "Live tracking depends on a compatible
//     location input the school supplies." NEVER imply supplied hardware, dispatch, or that the
//     platform certifies a vehicle.
//   • CLAUDE.md: `driver` + `bus_attendant` are minimal capture bundles (bustracking read/update,
//     transport:read, tasks:read/update) with NO student directory or fees; crew records link to
//     logins only via `POST /transport/drivers/{id}/link-user`.
//   • Transport fees are billed as their own fee head (taxable, unlike tuition) — `core/fee_gst.py`
//     + the transport reclass in the GL registers.
//   • `bustracking` is a plan-gated capability (tier restructure 2026-07-13, Growth+).
// NO safety outcome claims, no hardware promise, no compliance/certification claim.

const article = {
  slug: "school-transport-seat-safety",
  title: "Transport that refuses to overfill a bus",
  seoTitle: "School Transport: Seat Safety and Records | Vidyapeeth360",
  description:
    "Seat capacity as a hard limit, fleet document expiry you find early, and a boarding log that means something — school transport as a records problem.",
  topic: "Staff & operations",
  topicKey: "operations",
  readingMinutes: 5,
  published: "2026-08-18",
  updated: "2026-08-18",
  standfirst:
    "A school bus route is a safety commitment that is administered as a spreadsheet. The gap between those two facts is where an extra child on a full bus, and an expired fitness certificate, both live.",
  moduleLinks: ["transport-management", "transport-fees", "student-attendance"],
  conversion: {
    to: "/contact",
    label: "Talk through your routes and fleet records",
    note: "We will be explicit about what needs a location input you supply, and what works from records alone.",
  },
  sections: [
    {
      id: "capacity",
      heading: "Seat capacity is a limit, not a note",
      blocks: [
        {
          t: "p",
          text: "Every school knows how many children its buses seat. Very few schools have a system that refuses the allocation which exceeds it. The number lives in a register; the allocation happens at a counter, in August, under pressure from a parent who lives on the route.",
        },
        {
          t: "p",
          text: "So the first thing to fix is not tracking. It is that capacity should be enforced where the allocation is made. When the route is full, the answer should be a waitlist, not a judgement call — and the waitlist should be visible enough that the school can add a stop or a vehicle instead of quietly adding a child.",
        },
      ],
    },
    {
      id: "documents",
      heading: "The fleet is a set of expiry dates",
      blocks: [
        {
          t: "p",
          text: "A bus is legal to run only while a stack of documents is current. Each of them expires on its own date, and none of them announce themselves.",
        },
        {
          t: "ul",
          items: [
            "**Registration certificate** for each vehicle, with the record kept against the vehicle rather than in a folder.",
            "**Insurance**, the expiry a school least wants to discover after an incident rather than before one.",
            "**Permit and fitness certificate**, both dated, both routinely discovered expired at a checkpoint.",
            "**Pollution Under Control (PUC) certificate**, which is short-dated and therefore comes round again well before the others do.",
            "**Driver and attendant licences**, which expire per person and not per vehicle — a detail that makes a purely vehicle-shaped register incomplete.",
          ],
        },
        {
          t: "p",
          text: "The mechanism is unglamorous: hold the dates as data, alert before they pass, and make somebody accountable for the alert. A calendar reminder in one person's phone is not a system; it is that person.",
        },
      ],
    },
    {
      id: "tracking-honesty",
      heading: "Be honest about what live tracking is",
      blocks: [
        {
          t: "p",
          text: "Parents want to know where the bus is. That expectation is reasonable and it is also the place where school-transport software most often oversells.",
        },
        {
          t: "p",
          text: "A position on a map comes from a device on the vehicle reporting its location — a tracker the school buys and fits, or an app on a crew member's phone. Software displays and interprets that stream; it does not create it. If no device is reporting, the honest interface says so.",
        },
        {
          t: "note",
          title: "The stale-position problem",
          text: "The dangerous failure is not \"no data\". It is a map showing the bus at a junction it left eleven minutes ago, with nothing on screen to say the reading is old. A parent then makes a decision — send the child down, or not — on a stale fact presented as a live one. Any tracking view should badge its own freshness, and go visibly stale rather than quietly persist.",
        },
      ],
    },
    {
      id: "boarding-log",
      heading: "Who got on, and when",
      blocks: [
        {
          t: "p",
          text: "The record that actually matters after an incident is not the route map. It is whether a specific child boarded a specific bus at a specific stop, and who recorded it.",
        },
        {
          t: "ol",
          items: [
            "**Capture at the door**, by the attendant, on the trip — not reconstructed in the office that evening from memory.",
            "**Make the log append-only.** A boarding record that can be edited afterwards is a record that answers no question it was created to answer.",
            "**Give the crew the narrowest possible access.** A driver or attendant needs their trip and their boarding list. They do not need the student directory, guardian phone numbers or fee records, and giving them a general staff login to solve a capture problem is a data-protection decision made by accident.",
            "**Reconcile with attendance, do not replace it.** Boarding the bus is not the same fact as being present in class, and merging them hides the case you most want to see: the child who boarded and never arrived.",
          ],
        },
      ],
    },
    {
      id: "in-the-product",
      heading: "How this works in Vidyapeeth360",
      blocks: [
        {
          t: "p",
          text: "Route allocation is refused above the configured seat capacity — the request becomes a waitlist entry rather than a silent overfill, so the decision to add a stop, a trip or a vehicle is made deliberately. Transport is billed as its own fee head rather than folded into tuition, which keeps the tax treatment and the income reporting separate and correct.",
        },
        {
          t: "p",
          text: "The fleet and crew registers hold expiry dates for registration, insurance, permit, pollution and fitness certificates and for driver and attendant licences, and raise alerts before they pass. What the platform does not do is certify anything about a vehicle: the dates are yours, the documents are yours, and the alert is a reminder, not an approval.",
        },
        {
          t: "p",
          text: "Where a school supplies a compatible location input, trips show a live position with an explicit live-or-stale badge, so a parent is never shown an old reading as a current one. Boarding is captured on the trip and the log is append-only. Drivers and attendants get a deliberately minimal login — their trips, their boarding capture and their assigned tasks, with no student directory and no fees — and those logins exist only when a school explicitly links a crew record to a user account.",
        },
        {
          t: "p",
          text: "Everything else is ordinary record-keeping: routes and stops in [transport management](/modules/transport-management), the recurring charge in [transport fees](/modules/transport-fees), and the child's arrival still recorded through [student attendance](/modules/student-attendance) rather than inferred from a bus.",
        },
      ],
    },
  ],
  sources: [],
};

export default article;
